/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./me.css";
import type { Couple, Partner } from "./types";
import { useMeCouple, useCouplePreview } from "./meHooks";
import { getMeLayoutVariant } from "./layout";
import { MeRoutes } from "./routes";
import { MeAuthProvider, useMeAuth } from "./auth/MeAuthContext";
import { getMeTokenBinding } from "./auth/meToken";
import { MeAuthLanding } from "./auth/MeAuthLanding";
import { redeemSessionTokenMeSessionTokenRedeemPost } from "../__generated__/api/me/me";
import type { SessionTokenRedeemOut } from "../__generated__/api/wrenAPI.schemas";
import type { CustomRequestInit } from "../api/client";
import { PHProvider } from "../shared/PostHogProvider";
import { resetAnalytics, useMeAnalyticsIdentity } from "./analytics/track";

interface MeContextValue {
  couple: Couple | null;
  /** The acting partner, resolved from the couple's `actingPartnerId`. */
  partner: Partner | null;
  loading: boolean;
}

const MeContext = createContext<MeContextValue | null>(null);

export function useMeContext(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error("useMeContext must be used within MeApp");
  }
  return ctx;
}

const queryClient = new QueryClient();

// N14 (contracts v4): the /me query keys are exact (§4.4 — no token suffix),
// so when the held token VALUE changes to a DIFFERENT identity (dev login,
// logout→login) the previous identity's cached reads must be dropped
// wholesale — otherwise the new identity sees stale data (e.g. the old
// couple's `homebase: true`) for up to staleTime.
//
// EXCEPTION (operator-hit regression): the pre-bind→post-bind UPGRADE
// (couple→person) is the SAME human binding their session for the first
// time — there is no prior person-scoped data to leak, and clear() REFETCHES
// still-mounted queries, so the still-active pre-bind useCouplePreview
// refired /me/couple/preview with the fresh person token → 401
// (couple-token-only endpoint) → customInstance wiped the session
// mid-onboarding. Skip the clear on the upgrade; everything else
// (person→person, any →null logout) still clears.
function clearMeQueryCache(next: string | null, previous: string | null) {
  if (getMeTokenBinding(previous) === "couple" && getMeTokenBinding(next) === "person") {
    return;
  }
  queryClient.clear();
  // Same rule for analytics: an identity switch starts a fresh distinct id,
  // the pre-bind→post-bind upgrade (returned above) keeps the anonymous steps.
  if (previous) resetAnalytics();
}

/**
 * Loads the couple and resolves the acting partner, then renders the onboarding
 * flow. The shared-link self-select model (WP-A/D) runs in two token phases:
 *
 *   • PRE-BIND (couple token): the opener self-selects at S2. We read the
 *     least-privilege roster (`useCouplePreview`) and leave `partner` null —
 *     nobody is bound yet, so S2 shows both partners enabled.
 *   • POST-BIND (person token): S2's select-partner minted a person token, so
 *     the full couple read (`useMeCouple`) takes over and `partner` resolves
 *     off the server-authoritative `actingPartnerId`.
 *
 * The two queries are mutually `enabled`-gated on `binding` so the pre-bind
 * session never calls the person-only `/me/couple` (which would 401 + wipe the
 * session). Hooks are still called unconditionally (one is just disabled).
 *
 * (We do NOT decode the JWT for partner IDENTITY: the token carries the DB PK
 * `person_id`, while partners are keyed by `person_global_uuid`. We only read
 * its SCOPE to pick the phase; `actingPartnerId` resolves identity.)
 */
function MeShell() {
  const { binding } = useMeAuth();
  const preBind = binding === "couple";
  const { pathname } = useLocation();
  // ⚠ FROZEN post-WP-1 (contracts §4.2): MeShell branches ONLY this wrapper on
  // the layout seam — context/auth/two-phase gating above stay unchanged.
  const layoutVariant = getMeLayoutVariant(pathname);

  const full = useMeCouple({ enabled: !preBind });
  const preview = useCouplePreview({ enabled: preBind });

  const couple = preBind ? preview.data : full.data;
  const isLoading = preBind ? preview.isLoading : full.isLoading;
  const error = preBind ? preview.error : full.error;

  const partner = useMemo<Partner | null>(() => {
    if (!couple) return null;
    return couple.partners.find((p) => p.id === couple.actingPartnerId) ?? null;
  }, [couple]);

  useMeAnalyticsIdentity(couple ?? null, partner);

  const value = useMemo<MeContextValue>(
    () => ({ couple: couple ?? null, partner, loading: isLoading }),
    [couple, partner, isLoading]
  );

  // Distinguish a settled failure/empty result from an in-flight load: lumping
  // `!couple` with `isLoading` would show a perpetual "Loading…" if the couple
  // fetch errors or resolves empty. Only show the spinner while actually loading.
  if (isLoading) {
    return (
      // Cardless states, so they ARE the page: --rail-dark like the card's
      // surround, with the --text-on-dark ramp (9.74:1) rather than the light one,
      // which would be all but invisible here.
      <div className="min-h-dvh flex items-center justify-center bg-[var(--rail-dark)]">
        <p className="text-base text-[var(--text-on-dark-secondary)]">Loading…</p>
      </div>
    );
  }

  if (error || !couple) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-[var(--rail-dark)] px-6">
        <p className="text-base text-[var(--text-on-dark-secondary)] text-center">
          We couldn’t load your information. Please refresh, or check the link your therapist sent.
        </p>
      </div>
    );
  }

  if (layoutVariant === "hub") {
    // Homebase surfaces: full-bleed rail, one wide centered column (desktop =
    // one vertical column per the design). Flow pages may constrain their own
    // content width inside this column.
    //
    // Self-contained scroller (operator-hit): the global onboarding.css locks
    // the document body app-wide (position:fixed + 100dvh), so DOCUMENT
    // scroll never happens anywhere in this SPA — the /me shell must always
    // scroll INTERNALLY: the card variant via its overflow-y-auto column,
    // the hub via this fixed h-dvh + overflow-y-auto wrapper. min-h-dvh
    // relied on body scroll and clipped the hub at one viewport on device.
    return (
      <MeContext.Provider value={value}>
        {/* --canvas, the page ground ("is it the page, on the page, or receding?" in
            tokens.css). It was --muted, the recede token, which painted every hub
            page in the disabled/progress-track grey. */}
        <div className="h-dvh overflow-y-auto w-full bg-[var(--canvas)]">
          <div className="w-full max-w-[44rem] mx-auto">
            <MeRoutes />
          </div>
        </div>
      </MeContext.Provider>
    );
  }

  // Card variant: the onboarding / auth chrome.
  //
  // THE PAGE IS --rail-dark, the card stays --surface (operator, 2026-09-01).
  // Two separate changes, both off --muted, the recede token ("hover feedback,
  // disabled fill, progress tracks"):
  //
  //   The PAGE was --muted, a light grey that read as an absence of a decision.
  //   --rail-dark is the brand dark the therapist splash and the portal rail
  //   already stand on, so the card now sits on the same ground as the rest of
  //   the product instead of on nothing.
  //
  //   The CARD had a fill only from md up, so on mobile, where it is full-bleed
  //   and there is no page to see, --muted WAS the card and the whole flow was
  //   painted in the disabled-state grey. It is --surface at every width now,
  //   which is what the type ramp and every DS component in the flow are
  //   calibrated against.
  return (
    <MeContext.Provider value={value}>
      {/* iOS Safari (operator live-verified): h-dvh binds the shell to the
          DYNAMIC viewport so the footer CTA is never behind the collapsing
          toolbar; min-h-0 lets the flex-1 column bound its internal scroller.
          md keeps the centered-card behavior (h-auto + min-h-dvh). */}
      <div className="h-dvh flex flex-col bg-[var(--rail-dark)] md:h-auto md:min-h-dvh md:items-center md:justify-center md:py-8 md:px-4">
        {/* overflow-y-auto (mobile): fallback scroller for steps that don't
            bring StepShell's internal scroller (S11 is a plain div) — without
            it their content clips unscrollably inside the fixed h-dvh card.
            md keeps overflow-hidden; StepShell scrolls the card internally.

            ONE HEIGHT, NOT A RANGE. This was `min-h-[680px] max-h-[92vh]`, so the
            card sized itself to each step and the flow moved underneath the reader.
            Measured across all eleven steps at 1440x900: ten rendered at 680px with
            the primary at y=710, and focus_areas — the only step whose content
            passes the floor — grew to 828 and put its primary at y=784. So every
            visit to that one step slid the button 74px down the screen and back up
            again on leaving.

            A fixed height is what "the CTA does not move" actually requires. A
            shared MAX is not enough: the steps were already sharing one, and they
            differed anyway because they were free to be shorter than it.

            `min(680px, 92vh)` and not a bare 680: the cap was doing a second job
            besides growth, keeping the card inside a short window, and a flat 680
            would push it through the page's own py-8 gutters below ~744px tall. The
            card is still one height on any given screen, which is what alignment
            means; it just is not taller than the screen.

            The cost is carried by focus_areas alone, and deliberately: its eight
            options now scroll further inside a 680px card. That is survivable
            because its question is pinned above the scroller and the Back/Continue
            row was always fixed, so what moves is the options and nothing else.

            E-SIGN IS NOT THIS CARD. It has its own `.esign-card` in esign.css, sized
            separately, and stays that way. */}
        <div className="bg-[var(--surface)] flex-1 min-h-0 w-full flex flex-col overflow-y-auto md:flex-none md:rounded-2xl md:shadow-2xl md:overflow-hidden md:max-w-[30rem] md:w-full md:h-[min(680px,92vh)]">
          <MeRoutes />
        </div>
      </div>
    </MeContext.Provider>
  );
}

/**
 * Redeems a person-link `?token=` (the short-TTL opaque capability a SmartInvite
 * or SMS deeplink carries, e.g. `/me/sessions/:uuid?token=<opaque>`) for the
 * durable person session, then renders the gated flow.
 *
 * The `?token` is AUTHORITATIVE — it always redeems, even when a session token
 * already sits in localStorage. `MeAuthContext` tracks token PRESENCE, not
 * validity, so an ambient token may be stale/expired (a returning client who
 * onboarded on this device) or belong to a DIFFERENT person (a shared device).
 * Gating on ambient auth would open the flow under a stale/wrong session and
 * 401 straight back to `?expired` — the exact recurring bug.
 *
 * The stale/wrong-token guarantee comes from ORDERING, not from children being
 * gated on `status` forever: `MeAuthGate` checks `?token` FIRST, so while a
 * token is present ONLY this redeemer runs, and its `status` gates the transient
 * pre-scrub window (it shows "Verifying…", never children, until redemption). On
 * success it establishes the fresh session and scrubs `?token` from the URL —
 * which drops `linkToken`, so `MeAuthGate` re-renders past this redeemer and
 * renders children through its `isAuthenticated` path, now backed by the
 * freshly-redeemed token. A stale ambient token can therefore never render
 * children: the redeemer has already swapped in the fresh session before the
 * gate falls through to `isAuthenticated`.
 *
 * Mirrors `MeAuthLanding`'s couple-link care: persist the minted session token
 * BEFORE stripping the URL token so no re-render can drop it, and scrub the
 * bearer from the address bar on BOTH paths (success strips in place; failure
 * redirects to the token-less `?nosession`). `skip401Redirect` keeps a bad link
 * from clearing a session or bouncing to the global 401 handler — this component
 * owns the failure. The redeem endpoint is replay-tolerant within TTL, so
 * re-redeeming over a valid same-person session (and a StrictMode double-invoke)
 * is safe.
 */
function MePersonLinkRedeemer({
  token,
  children,
}: {
  token: string;
  children: ReactNode;
}) {
  const { setToken } = useMeAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<"pending" | "redeemed" | "failed">(
    "pending"
  );

  useEffect(() => {
    let active = true;
    redeemSessionTokenMeSessionTokenRedeemPost(
      { token },
      { skip401Redirect: true } as CustomRequestInit
    )
      .then((response) => {
        if (!active) return;
        const data = response.data as SessionTokenRedeemOut;
        // Persist the session BEFORE stripping the URL token (order matters — a
        // re-render must never race the token out from under us).
        setToken(data.session_token);
        // Scrub the bearer from the address bar (same path, no `?token`).
        navigate(pathname, { replace: true });
        setStatus("redeemed");
      })
      .catch(() => {
        if (active) setStatus("failed");
      });
    return () => {
      active = false;
    };
  }, [token, setToken, navigate, pathname]);

  // Invalid or genuinely-expired deeplink token → the neutral "no session"
  // landing (NOT "Session expired"). The redirect to a token-less URL also
  // scrubs the bearer from the bar + history (fail-closed).
  if (status === "failed") {
    return <Navigate to="/me/auth?nosession" replace />;
  }

  // Gate on THIS deeplink's redemption — never on ambient `isAuthenticated`, so
  // a stale/other-person token can't open the flow before the authoritative
  // `?token` is redeemed.
  if (status === "redeemed") {
    return <>{children}</>;
  }

  return (
    // Same cardless treatment as the other four: --rail-dark with the on-dark
    // text colours, so a client redeeming a deeplink never sees a differently-
    // coloured page than the one they land on.
    <div className="min-h-dvh flex items-center justify-center bg-[var(--rail-dark)]">
      <p className="text-base text-[var(--text-on-dark-secondary)]">Verifying…</p>
    </div>
  );
}

/**
 * Auth gate — require a client-session token; otherwise route to the landing.
 *
 * A deeplink carrying a person-link `?token=` (SmartInvite/SMS) is AUTHORITATIVE:
 * always route it through the redeemer, even over an ambient session (which may
 * be stale, expired, or a different person's — `isAuthenticated` is mere token
 * PRESENCE). A no-session visit with no redeemable token is a first-ever/link-less
 * arrival, NOT an expiry — it lands on the neutral `?nosession` copy. "Session
 * expired" is reserved for a previously-valid session that timed out (the 401
 * interceptor's `?expired`).
 */
function MeAuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useMeAuth();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get("token");

  if (linkToken) {
    return <MePersonLinkRedeemer token={linkToken}>{children}</MePersonLinkRedeemer>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/me/auth?nosession" replace />;
  }
  return <>{children}</>;
}

/**
 * Top-level component for `/me/*`. Owns its OWN auth context + query client,
 * outside the homebase AuthGate (Option A — sibling route in `main.tsx`).
 */
export function MeApp() {
  return (
    <QueryClientProvider client={queryClient}>
      <MeAuthProvider onTokenChange={clearMeQueryCache}>
        <PHProvider>
          <Routes>
            {/* The magic-link landing is public (no token required to land). */}
            <Route path="auth" element={<MeAuthLanding />} />
            <Route
              path="*"
              element={
                <MeAuthGate>
                  <MeShell />
                </MeAuthGate>
              }
            />
          </Routes>
        </PHProvider>
      </MeAuthProvider>
    </QueryClientProvider>
  );
}
